import {inject, Injectable, OnDestroy} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {GroupService} from '@modules/groups-management';
import {of, Subscription} from 'rxjs';
import {catchError, map, switchMap, take} from 'rxjs/operators';
import {cloneDeep} from 'lodash-es';
import {AnyLinkType} from 'shared/pretty-easy-navigation/models';
import {PrettyEasyNavigationService} from 'shared/pretty-easy-navigation/pretty-easy-navigation.service';
import {GuestMenu, LearnerMenu, TrainerMenu} from './models';
import {MathiaMenuClass, prefilterMathiaModelsMenu} from './mathia-models-menu';
import {MathiaSearchDataService} from '../search/mathia-search-data.service';
import {MATHIA_PARCOURS_SEARCH_FILTER_CONFIG} from '../search/mathia-search-filter-config';
import type {SearchFilterDefinition, SearchFilterValue} from 'shared/search-engine';

type MenuName = 'guest' | 'learner' | 'trainer';

@Injectable({
    providedIn: 'root'
})
export class PrettyEasyMenuInitializerService implements OnDestroy {
    private readonly prettyEasyNavigationService = inject(PrettyEasyNavigationService);
    private readonly authService = inject(AuthenticationService);
    private readonly groups = inject(GroupService);
    private readonly searchData = inject(MathiaSearchDataService);

    private currentMenu: MenuName = 'guest';
    private currentClasses: readonly MathiaMenuClass[] = [];
    private definitions: readonly SearchFilterDefinition<SearchFilterValue>[] = [];
    private readonly groupsSubscription: Subscription;

    constructor() {
        this.groupsSubscription = this.groups.onGroupsChanged.pipe(
            switchMap(groups => {
                const definitions$ = groups.length > 0
                    ? this.searchData.getFilterDefinitions({}, MATHIA_PARCOURS_SEARCH_FILTER_CONFIG, 'lesson').pipe(
                        take(1),
                        catchError(() => of([] as SearchFilterDefinition<SearchFilterValue>[])),
                    )
                    : of([] as SearchFilterDefinition<SearchFilterValue>[]);

                return definitions$.pipe(map(definitions => ({groups, definitions})));
            }),
        ).subscribe(({groups, definitions}) => {
            this.currentClasses = groups;
            this.definitions = definitions;
            this.refreshMenu();
        });
    }

    public ngOnDestroy(): void {
        this.groupsSubscription.unsubscribe();
    }

    public initializeMenu(): void {
        this.setMenu('guest');
    }

    public setMenu(menuName: MenuName): void {
        this.currentMenu = menuName;
        this.prettyEasyNavigationService.setMenu(this.buildMenu(menuName));
    }

    public refreshMenu(): void {
        this.setMenu(this.currentMenu);
    }

    public setMenuFromAccessLevel(): void {
        if (this.authService.hasLevel(['trainer', 'manager', 'administrator'])) {
            this.setMenu('trainer');
            return;
        }

        if (this.authService.hasLevel(['learner', 'parent'])) {
            this.setMenu('learner');
            return;
        }

        this.setMenu('guest');
    }

    private buildMenu(menuName: MenuName): AnyLinkType[] {
        return prefilterMathiaModelsMenu(cloneDeep(this.getBaseMenu(menuName)), this.currentClasses, this.definitions);
    }

    private getBaseMenu(menuName: MenuName): AnyLinkType[] {
        switch (menuName) {
            case 'learner':
                return LearnerMenu;
            case 'trainer':
                return TrainerMenu;
            default:
                return GuestMenu;
        }
    }
}
